import React from "react";
import { Popover } from "antd";

const VISIBLE_GATE_LIMIT = 3;

const isBlank = (value) =>
  value === null ||
  value === undefined ||
  (typeof value === "string" && value.trim() === "");

const formatScalar = (value) => {
  if (isBlank(value)) return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    return value.map(formatScalar).filter(Boolean).join(", ");
  }
  if (typeof value === "object") {
    const named = value.name ?? value.label ?? value.value;
    if (named !== undefined && typeof named !== "object") {
      return String(named).trim();
    }
    return Object.entries(value)
      .filter(([, item]) => item !== null && item !== undefined && typeof item !== "object")
      .map(([key, item]) => `${key}: ${item}`)
      .join(", ");
  }
  return String(value).trim();
};

const statusKind = (value) => {
  const status = String(value || "").trim().toLowerCase();
  if (["yes", "true", "pass", "passed", "complete", "completed", "met", "fully meets"].includes(status)) {
    return "yes";
  }
  if (["no", "false", "fail", "failed", "incomplete", "doesn't meet", "doesnt meet"].includes(status)) {
    return "no";
  }
  if (["warning", "pending", "partial", "partially meets"].includes(status)) {
    return "warning";
  }
  return "neutral";
};

const parseEntry = (entry) => {
  if (entry && typeof entry === "object" && !Array.isArray(entry)) {
    const name = formatScalar(
      entry.name || entry.gate || entry.gateName || entry.label || entry.key || ""
    );
    const value = formatScalar(
      entry.value ?? entry.status ?? entry.result ?? entry.dataIndex ?? entry.data
    );
    if (!name && !value) return null;
    return { name: name || value, value: name ? value : "" };
  }

  const text = formatScalar(entry);
  if (!text) return null;
  const colon = text.indexOf(":");
  if (colon > 0 && colon < text.length - 1) {
    return {
      name: text.slice(0, colon).trim(),
      value: text.slice(colon + 1).trim(),
    };
  }
  return { name: text, value: "" };
};

const splitCombined = (text) => {
  if (text.includes("\n")) return text.split(/\n+/).map((item) => item.trim()).filter(Boolean);
  if (text.includes("|")) return text.split(/\s*\|\s*/).filter(Boolean);
  if (text.includes(";")) return text.split(/\s*;\s*/).filter(Boolean);
  if ((text.match(/:/g) || []).length > 1 && text.includes(",")) {
    return text.split(/,\s*(?=[^,]+:)/).map((item) => item.trim()).filter(Boolean);
  }
  if (text.includes(",")) return text.split(",").map((item) => item.trim()).filter(Boolean);
  return [text];
};

export const parseGatesValue = (value) => {
  if (isBlank(value)) return [];

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (
      (trimmed.startsWith("[") && trimmed.endsWith("]")) ||
      (trimmed.startsWith("{") && trimmed.endsWith("}"))
    ) {
      try {
        return parseGatesValue(JSON.parse(trimmed));
      } catch (error) {
        // Treat it as plain text when it is not valid JSON.
      }
    }
    return splitCombined(trimmed).map(parseEntry).filter(Boolean);
  }

  if (Array.isArray(value)) {
    return value.map(parseEntry).filter(Boolean);
  }

  if (typeof value === "object") {
    const looksLikeSingle =
      "name" in value ||
      "gate" in value ||
      "gateName" in value ||
      "label" in value ||
      "value" in value ||
      "status" in value ||
      "result" in value ||
      "dataIndex" in value;
    if (looksLikeSingle) {
      const entry = parseEntry(value);
      return entry ? [entry] : [];
    }
    return Object.entries(value)
      .map(([name, raw]) => ({ name, value: formatScalar(raw) }))
      .filter((item) => item.name || item.value);
  }

  const entry = parseEntry(value);
  return entry ? [entry] : [];
};

export const isGatesPreviewField = (fieldId) => {
  const key = String(fieldId || "")
    .replace(/[\s_-]+/g, "")
    .toLowerCase();
  return key === "gates" || key === "gatesandresults" || key === "gatesresults";
};

export const reportIncludesGates = (fields = []) =>
  (Array.isArray(fields) ? fields : []).some((field) => {
    const key =
      typeof field === "string"
        ? field
        : field?.key || field?.id || field?.field || "";
    return isGatesPreviewField(key);
  });

const GateChip = ({ item }) => {
  const kind = statusKind(item.value);
  const title = item.value ? `${item.name}: ${item.value}` : item.name;
  return (
    <span className={`gate-chip is-${kind}`} title={title}>
      <span className="gate-chip-name">{item.name}</span>
      {item.value ? <span className="gate-chip-value">{item.value}</span> : null}
    </span>
  );
};

const GatesPreviewCell = ({ value }) => {
  const items = parseGatesValue(value);
  if (!items.length) return "-";

  const visible = items.slice(0, VISIBLE_GATE_LIMIT);
  const hidden = items.slice(VISIBLE_GATE_LIMIT);

  return (
    <div className="gates-preview">
      {visible.map((item, index) => (
        <GateChip key={`${item.name}-${index}`} item={item} />
      ))}
      {hidden.length > 0 && (
        <Popover
          trigger="click"
          placement="bottomLeft"
          overlayClassName="gates-preview-popover"
          content={
            <div className="gates-preview-list">
              {items.map((item, index) => (
                <GateChip key={`all-${item.name}-${index}`} item={item} />
              ))}
            </div>
          }
        >
          <button type="button" className="gate-chip gate-chip-more">
            +{hidden.length} more
          </button>
        </Popover>
      )}
    </div>
  );
};

export default GatesPreviewCell;
